import clsx from 'clsx';
import { useLocalStorageState } from 'ahooks';
import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';
import { useEffect, useRef, useState } from 'react';

import { Instruction } from '@/components/instruction';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Button } from '@flintmint/web-shared/components/button';
import { NoData } from '@flintmint/web-shared/components/no-data';
import { Collapse } from '@flintmint/web-shared/components/collapse';

import { useInstructions } from '@/hooks/rxdb-models';

import { itemTypes } from '@/constants/item-types';

import type { DocumentWithIdNameContent } from '@/constants/types';

import styles from './instructions-panel.module.scss';

export const InstructionsPanel = (props: InstructionsPanelProps) => {
  const {
    active,
    readOnly,
    groupToExpand,
    focusedBlockId,
    enabled = true,
    instructionToFocus,
    onInstructionDelete,
    onInstructionUpdate,
    appearance = 'list',
    resetFocusedBlockId,
    documentInstructions,
    groupedInstructions = [],
    onInstructionEnabledChange,
    onAddInstruction,
    isLoading,
    editorTestId,
    view = 'editor',
  } = props;

  const [focusId, setFocusId] = useState<string>();
  const [collapsedInstructionIds, setCollapsedInstructionIds] = useLocalStorageState<string[]>(
    'instructions-panel-collapsedInstructionIds',
    { defaultValue: [] }
  );
  const [collapsedGroups, setCollapsedGroups] = useLocalStorageState<string[]>('instructions-panel-collapsedGroups', {
    defaultValue: [],
  });

  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const { showToastNotification } = useToaster();

  const groupInstructionsMap = groupedInstructions.reduce((acc, group) => ({ ...acc, ...group.instructions }), {});
  const instructionIds = Object.keys({ ...documentInstructions, ...groupInstructionsMap });

  const { data: fetchedInstructions = [], isLoading: isLoadingInstructions } = useInstructions(
    {
      ids: instructionIds,
      sort: [{ createdAt: 'asc' }],
    },
    {
      listen: enabled,
      keepPreviousWhileLoading: true,
    }
  );

  useEffect(() => {
    if (!groupToExpand) return;
    setCollapsedGroups((prev = []) => prev.filter((id) => id !== groupToExpand));
  }, [groupToExpand]);

  useEffect(() => {
    if (!instructionToFocus) return;
    const timeout = setTimeout(() => setFocusId(instructionToFocus), 300);
    return () => clearTimeout(timeout);
  }, [instructionToFocus]);

  useEffect(() => {
    if (active && focusedBlockId && groupedInstructions?.some((g) => g.id === focusedBlockId)) {
      if (collapsedGroups?.includes(focusedBlockId)) {
        setCollapsedGroups((prev = []) => prev.filter((id) => id !== focusedBlockId));
      }

      const groupElement = groupRefs.current[focusedBlockId];

      if (groupElement) {
        requestAnimationFrame(() => groupElement.scrollIntoView({ behavior: 'smooth', block: 'center' }));
      }
    }
  }, [active, focusedBlockId, groupedInstructions, setCollapsedGroups, collapsedGroups]);

  const handleDelete = async (instructionId: string, isBlockInstruction: boolean) => {
    if (isBlockInstruction) {
      showToastNotification({
        type: 'info',
        title: 'Delete unavailable',
        description: `Block instructions can only be deleted from the master block.`,
      });
      return false;
    }
    setCollapsedInstructionIds((val) => (val || []).filter((id) => id !== instructionId));
    await onInstructionDelete?.(instructionId);
    return true;
  };

  const toggleExpanded = (id: string) => {
    setCollapsedInstructionIds((prev = []) => (prev.includes(id) ? prev.filter((key) => key !== id) : [...prev, id]));
  };

  const toggleGroupCollapse = (groupId: string) => {
    setCollapsedGroups((prev = []) => (prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]));
  };

  const renderInstruction = (
    instruction: DocumentWithIdNameContent,
    blockId?: string,
    groupInstructions?: Record<string, boolean>
  ) => {
    return (
      <Instruction
        readOnly={readOnly}
        key={instruction.id}
        instruction={instruction}
        toggleExpanded={toggleExpanded}
        focused={focusId === instruction.id}
        showDeleteConfirmation={!blockId}
        onInstructionUpdate={onInstructionUpdate}
        removeExternalFocus={() => setFocusId(undefined)}
        expanded={!collapsedInstructionIds?.includes(instruction.id)}
        onDelete={(instructionId) => handleDelete(instructionId, !!blockId)}
        enabled={blockId ? !!groupInstructions?.[instruction.id] : !!documentInstructions?.[instruction.id]}
        onEnabledChange={(instructionId, enabled) => onInstructionEnabledChange(instructionId, enabled, blockId)}
        editorTestId={editorTestId}
        expandArrowTestId={`${view}-instruction-expand`}
      />
    );
  };

  const renderGroup = (group: GroupedInstruction) => {
    const instructionItems = fetchedInstructions.filter((doc) => group.instructions.hasOwnProperty(doc.id));
    if (instructionItems.length === 0) return null;

    const expanded = !collapsedGroups?.includes(group.id);

    return (
      <div
        key={group.id}
        data-testid={`group-${group.id}`}
        className={clsx(styles.instructionGroup, {
          [styles.focused]: focusedBlockId === group.id,
        })}
        ref={(el) => {
          groupRefs.current[group.id] = el;
        }}
        onAnimationEnd={resetFocusedBlockId}
      >
        <ul
          className={clsx(styles.instructionGroupHeader, {
            [styles.expanded]: expanded,
          })}
          onClick={() => toggleGroupCollapse(group.id)}
        >
          <li className="section-info">
            <Icon type={itemTypes[group.variant || group.type]?.icon} size={16} className={styles.typeIcon} />
            <p className="ellipsis-text">{`${group.name} Instructions`}</p>
            <small className="badge">{instructionItems.length}</small>
          </li>
          <li data-testid="group-expand">
            <Icon type="arrow-up-01-sharp" className={styles.expandIcon} />
          </li>
        </ul>
        <Collapse
          open={expanded}
          className={clsx(styles.instructionsCollapse, {
            [styles.grid]: appearance === 'grid',
          })}
        >
          {instructionItems.map((instruction) =>
            renderInstruction(instruction, group.type === 'blocks' ? group.id : undefined, group.instructions)
          )}
        </Collapse>
      </div>
    );
  };

  if (!fetchedInstructions.length) {
    if (isLoading || isLoadingInstructions) return null;

    return (
      <NoData
        icon={<Icon type="3rd-bracket-bulk" size={60} multiColor />}
        description={`Add instructions here to guide\n the AI how to generate your content`}
        actionRenderer={() => (
          <Button
            appearance="outline"
            color="dark"
            onClick={onAddInstruction}
            data-testid={view === 'block' ? 'block-add-instruction' : 'add-instruction'}
          >
            Add Instruction
          </Button>
        )}
      />
    );
  }

  return !!groupedInstructions.length
    ? groupedInstructions.map(renderGroup)
    : fetchedInstructions.map((instruction) => renderInstruction(instruction));
};

export interface InstructionsPanelProps {
  readOnly?: boolean;
  appearance?: 'list' | 'grid';
  groupToExpand?: string;
  instructionToFocus?: string;
  active?: boolean;
  enabled?: boolean;
  focusedBlockId?: string;
  regenerateDisabled?: boolean;
  resetFocusedBlockId?: () => void;
  onInstructionUpdate?: () => void;
  groupedInstructions?: GroupedInstruction[];
  documentInstructions?: Record<string, boolean>;
  onAddInstruction: () => void;
  isLoading?: boolean;
  editorTestId?: string;
  view?: 'editor' | 'block';
  onInstructionDelete?: (instructionId: string) => void | Promise<void>;
  onInstructionEnabledChange: (instructionId: string, enabled: boolean, blockId?: string) => Promise<any>;
}

export type GroupedInstruction = {
  id: string;
  name: string;
  type: string;
  variant?: string;
  instructions: Record<string, boolean>;
};
